import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Ship, Anchor, Sailboat, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Services data for Manifest List
const services = [
  {
    id: '01',
    key: 'portAgency',
    title_en: 'Port Agency',
    title_ru: 'Портовое агентство',
    desc_en: 'Full-scope port agency operations across all Turkish ports. Customs clearance, vessel operations, crew changes.',
    desc_ru: 'Полный спектр портовых агентских операций во всех турецких портах.',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/95o1vrse_aerial-view-of-large-general-cargo-ship-tanker-bul-2023-11-27-05-02-09-utc-scaled.jpg',
  },
  {
    id: '02',
    key: 'cruiseAgency',
    title_en: 'Cruise Agency',
    title_ru: 'Круизное агентство',
    desc_en: 'Specialized cruise ship handling. Passenger logistics, shore excursions, VIP services.',
    desc_ru: 'Специализированное обслуживание круизных судов. Логистика пассажиров.',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/fu70ioar_cruise-ship-cruise-liners-beautiful-white-cruise-2024-12-08-00-31-58-utc-scaled.jpg',
  },
  {
    id: '03',
    key: 'yachtAgency',
    title_en: 'Yacht Agency',
    title_ru: 'Яхтенное агентство',
    desc_en: 'Premium yacht services. Berthing, provisioning, concierge, technical support.',
    desc_ru: 'Премиальные яхтенные услуги. Стоянка, снабжение, консьерж.',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/lmdb61r2_luxury-yacht-and-blue-sea-at-sunset-in-summer-aer-2024-11-26-02-27-18-utc-scaled.jpg',
  },
  {
    id: '04',
    key: 'shipSupply',
    title_en: 'Ship Supply',
    title_ru: 'Судовое снабжение',
    desc_en: 'Complete ship chandlery. Technical stores, provisions, spare parts delivery.',
    desc_ru: 'Полное судовое снабжение. Технические запасы, провизия.',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/dg6dn9wz_various-food-ingredients-2024-10-22-10-40-13-utc-scaled.jpg',
  },
];

export const Home = () => {
  const { language } = useLanguage();
  const [activeService, setActiveService] = useState(0);
  const [counters, setCounters] = useState({ operations: 0, clients: 0, ports: 0, years: 0, vessels: 0 });
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  // Counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
          const targets = { operations: 450, clients: 15, ports: 30, years: 35, vessels: 5000 };
          Object.keys(targets).forEach((key) => {
            let current = 0;
            const increment = targets[key] / 40;
            const timer = setInterval(() => {
              current += increment;
              if (current >= targets[key]) {
                current = targets[key];
                clearInterval(timer);
              }
              setCounters((prev) => ({ ...prev, [key]: Math.round(current) }));
            }, 50);
          });
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsVisible]);

  return (
    <main data-testid="home-page" className="min-h-screen">
      {/* HERO - Full Screen Video with Split Content */}
      <section
        data-testid="hero-section"
        className="min-h-screen relative"
      >
        {/* Video Background - Full Screen */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/95o1vrse_aerial-view-of-large-general-cargo-ship-tanker-bul-2023-11-27-05-02-09-utc-scaled.jpg"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/l72lvimi_DJI_0121.MP4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy/40 z-10" />
        
        {/* Content Grid */}
        <div className="relative z-20 min-h-screen flex flex-col lg:flex-row">
          {/* Left - Typography */}
          <div className="flex-1 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-32 lg:py-0">
            <div className="max-w-xl">
              <p className="text-editorial text-white/80 mb-8 animate-fade-in-up">
                {language === 'en' ? 'Maritime Agency • Since 1990' : 'Морское агентство • С 1990'}
              </p>
              
              <h1 className="text-massive text-white mb-8 animate-fade-in-up animation-delay-100 drop-shadow-lg">
                ROTA
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-12 animate-fade-in-up animation-delay-200">
                {language === 'en' 
                  ? 'The silent force connecting vessels to Turkish ports. Three decades of maritime excellence from the Black Sea.'
                  : 'Невидимая сила, соединяющая суда с турецкими портами. Три десятилетия морского мастерства с Черного моря.'}
              </p>
              
              <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
                <Link to="/contact">
                  <button data-testid="hero-cta-primary" className="btn-signal">
                    {language === 'en' ? 'Request Quote' : 'Запрос'}
                    <ArrowRight className="inline ml-2 w-4 h-4" />
                  </button>
                </Link>
                <Link to="/about">
                  <button data-testid="hero-cta-secondary" className="border-2 border-white text-white hover:bg-white hover:text-navy uppercase tracking-[0.2em] text-[11px] font-bold px-8 py-4 transition-all duration-300">
                    {language === 'en' ? 'Our Story' : 'О нас'}
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right - Stats Box */}
          <div className="flex-1 relative flex items-end justify-end p-8 lg:p-16">
            <div className="bg-navy/60 backdrop-blur-md border border-white/20 p-8 w-full max-w-md">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="font-mono text-2xl lg:text-3xl font-bold text-white">+450K</p>
                  <p className="text-editorial text-white/70 mt-2">
                    {language === 'en' ? 'Operations' : 'Операций'}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-2xl lg:text-3xl font-bold text-white">+15K</p>
                  <p className="text-editorial text-white/70 mt-2">
                    {language === 'en' ? 'Clients' : 'Клиентов'}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-2xl lg:text-3xl font-bold text-signal">+30</p>
                  <p className="text-editorial text-white/70 mt-2">
                    {language === 'en' ? 'Ports' : 'Портов'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES - Manifest List */}
      <section data-testid="services-section" className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - List */}
            <div>
              <p className="text-editorial text-slate-500 mb-8">
                {language === 'en' ? 'What We Do' : 'Наши услуги'}
              </p>
              <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-navy mb-12">
                {language === 'en' ? 'Services' : 'Услуги'}
              </h2>

              <div className="border-t border-slate-200">
                {services.map((service, index) => (
                  <div
                    key={service.key}
                    data-testid={`service-item-${service.key}`}
                    className={`manifest-item group ${activeService === index ? 'border-signal pl-8' : ''}`}
                    onMouseEnter={() => setActiveService(index)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-mono text-xs text-slate-400 block mb-2">
                          {service.id}
                        </span>
                        <h3 className="font-heading text-2xl font-bold text-navy group-hover:text-signal transition-colors">
                          {language === 'en' ? service.title_en : service.title_ru}
                        </h3>
                        <p className={`text-slate-600 mt-3 transition-all duration-500 ${
                          activeService === index ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
                        }`}>
                          {language === 'en' ? service.desc_en : service.desc_ru}
                        </p>
                      </div>
                      <ArrowUpRight className={`w-5 h-5 transition-all duration-300 ${
                        activeService === index ? 'text-signal rotate-0' : 'text-slate-300 -rotate-45'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/services" className="inline-block mt-8">
                <button className="btn-outline">
                  {language === 'en' ? 'All Services' : 'Все услуги'}
                </button>
              </Link>
            </div>

            {/* Right - Image */}
            <div className="relative hidden lg:block">
              <div className="aspect-square w-full overflow-hidden rounded-[20px]">
                {services.map((service, index) => (
                  <img
                    key={service.key}
                    src={service.image}
                    alt={service.title_en}
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
                      activeService === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS - Full Width Dark */}
      <section ref={statsRef} className="py-24 lg:py-32 bg-navy">
        <div className="container-bespoke">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
            <div className="text-center">
              <p className="font-mono text-4xl lg:text-5xl font-bold text-white mb-4">
                +{counters.operations}K
              </p>
              <p className="text-editorial text-slate-400 text-sm">
                {language === 'en' ? 'Completed Operations' : 'Выполненных операций'}
              </p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl lg:text-5xl font-bold text-white mb-4">
                +{counters.clients}K
              </p>
              <p className="text-editorial text-slate-400 text-sm">
                {language === 'en' ? 'Global Clients' : 'Глобальных клиентов'}
              </p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl lg:text-5xl font-bold text-white mb-4">
                +{counters.ports}
              </p>
              <p className="text-editorial text-slate-400 text-sm">
                {language === 'en' ? 'Ports Served' : 'Обслуживаемых портов'}
              </p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl lg:text-5xl font-bold text-white mb-4">
                {counters.years}+
              </p>
              <p className="text-editorial text-slate-400 text-sm">
                {language === 'en' ? 'Years of Excellence' : 'Лет совершенства'}
              </p>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <p className="font-mono text-4xl lg:text-5xl font-bold text-signal mb-4">
                {counters.vessels.toLocaleString()}+
              </p>
              <p className="text-editorial text-slate-400 text-sm">
                {language === 'en' ? 'Vessels Served' : 'Обслужено судов'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER - Asymmetric */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Logo Shape */}
            <div className="lg:col-span-5 lg:col-start-1">
              <div className="relative flex items-center justify-center">
                <div className="w-full h-[400px] lg:h-[500px] flex items-center justify-center bg-white rounded-[20px] shadow-lg">
                  <img
                    src="/logo.png"
                    alt="Rota Maritime Agency Logo"
                    className="w-3/4 h-auto object-contain"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-signal p-6 lg:p-8 rounded-lg">
                  <p className="font-mono text-3xl lg:text-4xl font-bold text-white">1990</p>
                  <p className="text-editorial text-white/80 mt-1">
                    {language === 'en' ? 'Established' : 'Основано'}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-editorial text-slate-500 mb-4">
                {language === 'en' ? 'About Rota' : 'О Rota'}
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-navy mb-6">
                {language === 'en' 
                  ? 'Founded by the Levend family in Sinop'
                  : 'Основано семьей Левенд в Синопе'}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {language === 'en'
                  ? 'Rota Maritime Agency has built a strong reputation based on professionalism, expertise, and customer satisfaction. Our dedicated team ensures seamless operations for vessels at all major ports throughout Turkey.'
                  : 'Rota Maritime Agency построило прочную репутацию, основанную на профессионализме, экспертизе и удовлетворенности клиентов.'}
              </p>
              <Link to="/about">
                <button className="btn-outline">
                  {language === 'en' ? 'Read Our Story' : 'Наша история'}
                  <ArrowRight className="inline ml-2 w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Minimal */}
      <section className="py-24 lg:py-32 bg-white border-t border-slate-200">
        <div className="container-bespoke text-center">
          <p className="text-editorial text-slate-500 mb-6">
            {language === 'en' ? 'Ready to work with us?' : 'Готовы работать с нами?'}
          </p>
          <h2 className="font-heading text-4xl lg:text-5xl xl:text-6xl font-extrabold text-navy mb-8">
            {language === 'en' ? 'Let\'s connect' : 'Свяжитесь с нами'}
          </h2>
          <Link to="/contact">
            <button className="btn-signal">
              {language === 'en' ? 'Get in Touch' : 'Связаться'}
              <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </main>
  );
};
